import type { ReactNode } from 'react';
import { ORIGIN_LABEL, type JobOrder } from '@/domain';
import { strings } from '@/shared/strings';
import { CaseMarks } from '@/ui/data/CaseMarks';
import styles from './OrderRow.module.css';

/**
 * A section heading, marked up as one. Payments and Shipping are lists of
 * groups, and a screen reader should be able to jump between them rather than
 * hearing four undifferentiated runs of orders.
 */
export function GroupLabel({ children, count }: { children: string; count: number }) {
  return (
    <h3 className={styles.group}>
      {children} <span className={styles.count}>{count}</span>
    </h3>
  );
}

export function EmptyGroup({ children }: { children: string }) {
  return <div className={styles.empty}>{children}</div>;
}

/**
 * One order on the Payments or Shipping list. The prototype's dpkRow.
 *
 * The identity line degrades rather than disappearing: a caller without
 * seeIdentity still needs to know which order this is and what is in it. The
 * doctor and the patient both, as every case table shows them; these two
 * screens are work queues with an action per row rather than lists to browse,
 * so they keep their cards and take the patient beside the doctor.
 *
 * A remake carries the mark every case table gives one, beside its number, so
 * the mark means the same thing on the cards as on the tables. The request of
 * 6 October 2026 (second), item 2.
 */
export function OrderRow({
  order,
  actions,
  muted = false,
}: {
  order: JobOrder;
  actions: ReactNode;
  muted?: boolean;
}) {
  /*
   * The units the case covers, across its kits: what the desk packs and the
   * doctor is billed for. It read the number of kits, which is one for every
   * case, so every row said 1 unit. The lab's request of 22 October 2026.
   */
  const units =
    order.units === undefined
      ? null
      : `${order.units} ${order.units === 1 ? strings.payments.unit : strings.payments.units}`;

  return (
    <div
      className={`${styles.row} ${muted ? styles.muted : ''}`}
      role="group"
      aria-label={order.jobOrderNo}
    >
      <span className={styles.jo}>
        <CaseMarks urgent={order.urgent} redo={order.redoOf !== null} held={order.hold.held} />
        {order.jobOrderNo}
      </span>
      <div className={styles.meta}>
        {order.dentistName ?? strings.orders.hidden} · {order.patientName ?? strings.orders.hidden}
        {units === null ? '' : ` · ${units}`} · {ORIGIN_LABEL[order.origin]}
      </div>
      <div className={styles.actions}>{actions}</div>
    </div>
  );
}

export { styles as orderRowStyles };
